<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted } from 'vue';
import { useApp } from '../stores/app';
import { formatBytes, t } from '../i18n';
import { storageHealth } from '../platform';
import { grantAccess, storagePlace } from '../storage-location';
import { exportOpenCanvasesNow } from '../archive';
import Icon from './Icon.vue';

/**
 * Honest warning when data cannot be kept: no browser storage (everything is in memory), a full
 * disk quota, a blocked database — or a small private-window-like storage. Shown on every page
 * until the cause is gone; the "limited" notice can be dismissed.
 */
const app = useApp();
const state = computed(() => storageHealth.state);
const visible = computed(() => state.value !== 'ok' && !(state.value === 'limited' && storageHealth.dismissed));
const severe = computed(() => state.value !== 'limited');
const text = computed(() => {
  switch (state.value) {
    case 'memory':
      return t('storage.healthUnavailable');
    case 'quota':
      return t('storage.healthQuota');
    case 'blocked':
      return t('storage.healthBlocked');
    case 'failed':
      return t('storage.healthFailed');
    case 'folder':
      return t('storage.healthFolder', { name: storagePlace.name });
    case 'limited':
      return t('storage.healthLimited', { quota: formatBytes(storageHealth.quota) });
    default:
      return '';
  }
});
const canExport = computed(() => app.sessions.size > 0);

function openStorage(): void {
  app.settingsSection = 'storage';
  app.go('settings');
}

/** Closing the tab (or the browser) now would lose work: ask first. */
function atRisk(): boolean {
  if (state.value === 'quota' || state.value === 'blocked' || state.value === 'failed' || state.value === 'folder') return true;
  if (state.value === 'memory') return app.canvases.length > 0 || app.sessions.size > 0;
  for (const s of app.sessions.values()) if (s.saveStatus.value !== 'saved' && !s.isReadOnly) return true;
  return false;
}
function onBeforeUnload(e: BeforeUnloadEvent): void {
  if (!atRisk()) return;
  e.preventDefault();
  e.returnValue = t('storage.leaveWarn');
}
/** Hiding the tab is the last reliable moment to write: flush every open canvas. */
function onHidden(): void {
  if (document.visibilityState === 'hidden') for (const s of app.sessions.values()) void s.flush().catch(() => undefined);
}
onMounted(() => {
  window.addEventListener('beforeunload', onBeforeUnload);
  document.addEventListener('visibilitychange', onHidden);
});
onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', onBeforeUnload);
  document.removeEventListener('visibilitychange', onHidden);
});
</script>

<template>
  <div v-if="visible" class="storage-banner" :class="{ severe }" role="alert">
    <Icon :name="severe ? 'warning' : 'info'" :size="18" />
    <div class="body">
      <strong v-if="severe">{{ t('storage.healthTitle') }}</strong>
      <span>{{ text }}</span>
    </div>
    <div class="actions">
      <button v-if="canExport" class="pill" @click="exportOpenCanvasesNow(app)"><Icon name="download" :size="14" />{{ t('storage.exportNow') }}</button>
      <button v-if="state === 'folder'" class="pill" data-act="grant-folder" @click="grantAccess()"><Icon name="folder" :size="14" />{{ t('storage.gate.grant') }}</button>
      <button v-if="state === 'quota'" class="pill" @click="openStorage"><Icon name="storage" :size="14" />{{ t('storage.openStorage') }}</button>
      <button v-if="!severe" class="pill icon" :aria-label="t('common.close')" @click="storageHealth.dismissed = true"><Icon name="close" :size="14" /></button>
    </div>
  </div>
</template>

<style scoped>
.storage-banner {
  position: fixed;
  z-index: 3000;
  left: 50%;
  bottom: 16px;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 12px;
  width: min(760px, calc(100% - 24px));
  padding: 10px 12px 10px 14px;
  border-radius: 18px;
  background: var(--panel);
  border: 1px solid var(--border-strong);
  color: var(--text);
  box-shadow: var(--float-shadow);
}
.storage-banner.severe {
  border-color: color-mix(in srgb, var(--danger) 55%, transparent);
  background: color-mix(in srgb, var(--danger) 9%, var(--panel));
}
.storage-banner.severe > svg {
  color: var(--danger);
}
.body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 12.5px;
  line-height: 1.4;
}
.body strong {
  font-size: 13px;
}
.actions {
  display: flex;
  gap: 6px;
  flex: none;
  flex-wrap: wrap;
  justify-content: flex-end;
}
@media (max-width: 640px) {
  .storage-banner {
    flex-wrap: wrap;
  }
}
</style>
